import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, filter, finalize, map, switchMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { PackageInterceptConfirmationModalComponent } from 'src/app/components/revenue-recovery/package-intercept-confirmation-modal/package-intercept-confirmation-modal.component';
import { Intercept } from 'src/app/components/shared/intercept';

import { SpecialCharacters } from 'src/app/core/enums';
import { CurrentLocation } from 'src/app/core/models/admin/location-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import {
  CountryLimits,
  RevenueRecoveryRequest,
  WWEFItem,
} from 'src/app/core/models/revenue-recovery/revenue-recovery';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { UnitConfigurationService } from 'src/app/core/services/common/unit-configuration/unit-configuration.service';
import { FileParseType } from '../../file-upload/file-upload.constants';
import { IOption } from '../../form/components/form-select/form-select.model';
import { BUTTON_CLICK } from '../../modal/exit-modal/exit-modal.constants';
import {
  IMAGE_SRC,
  NO_RESULTS,
  VALIDATION_MESSAGE,
} from '../../shared-constants';
import {
  maxValidator,
  nonZeroValidator,
  PtnRangePackageValidator,
} from '../../validators';
import {
  ColumnIds,
  ColumnText,
  CONST,
  COUNTRY_LIST,
  WWEF,
} from './world-wide-express-freight-table.constant';

@Component({
  selector: 'app-world-wide-express-freight-table',
  templateUrl: './world-wide-express-freight-table.component.html',
  styleUrls: ['./world-wide-express-freight-table.component.scss'],
  standalone: false,
})
export class WorldWideExpressFreightTableComponent
  extends Intercept
  implements OnInit
{
  private readonly dialog = inject(MatDialog);
  private readonly userService = inject(UserService);
  private readonly locationService = inject(LocationService);
  private readonly destroy = inject(DestroyRef);
  private readonly _unitConfigurationService = inject(UnitConfigurationService);
  private fb = inject(FormBuilder);

  readonly tableData = signal<WWEF[]>([]);
  readonly countryLimits = signal<CountryLimits[]>([]);
  readonly countryList = signal<IOption[]>([]);
  readonly selectedCountry = signal<CountryLimits | undefined>(undefined);
  readonly originCountry = signal<CountryLimits | undefined>(undefined);
  readonly currentLocation = signal<CurrentLocation | undefined>(undefined);
  readonly SRC = IMAGE_SRC;

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly COUNTRY_LIST = COUNTRY_LIST;
  readonly PARSE_TYPE = FileParseType;

  readonly CONST = { ...CONST, ...VALIDATION_MESSAGE };

  readonly SpecialCharacters = SpecialCharacters;

  dataSource = new MatTableDataSource<WWEF>();
  dropdownOpen = [false];

  unitConfig = this._unitConfigurationService.getUnitMappingByType(
    this._unitConfigurationService.unitOfMeasureSystem(),
  );

  // Create column search objects
  createTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  trackingNumber = this.createTableColumn(ColumnIds.TRACKING_NUMBER);
  sortDate = this.createTableColumn(ColumnIds.SORT_DATE);
  sortCode = this.createTableColumn(ColumnIds.SORT_CODE);
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  sortCodeOption: Items[] = [];
  form!: FormGroup;
  isFormSubmitted = signal(false);
  minSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  maxSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  tableMinSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  tableMaxSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  enabledDates: Date[] = [];

  /**
   * OnInit Lifecycle hook
   *
   * @memberof WorldWideExpressFreightTableComponent
   */
  ngOnInit(): void {
    this.initForm();
    this.formValueChanges();
    this.getCurrentUser();
    this.getCurrentLocation();
    this.setSortCodeOption();
    this.dataSource.filterPredicate = this.createFilter();
    this.getCountryLimits();
  }

  /**
   * get current user
   *
   * @memberof HandlingScanTableComponent
   */
  getCurrentUser() {
    this.userService
      .getCurrentUser$()
      .pipe(
        filter((user) => !!user && !!Object.keys(user).length),
        takeUntilDestroyed(this.destroy),
        switchMap((_user) => {
          return this.getIntercepts();
        }),
      )
      .subscribe();
  }

  getCurrentLocation() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((val) => {
        this.currentLocation.set(val);
        if (val.location?.locationDetails.country.unit) {
          this._unitConfigurationService.unitOfMeasureSystem.set(
            val.location?.locationDetails.country.unit,
          );
        }
        this.unitConfig = this._unitConfigurationService.getUnitMappingByType(
          this._unitConfigurationService.unitOfMeasureSystem(),
        );

        this.resetState();
      });
  }

  resetState() {
    this.tableData.set([]);
    this.dataSource.data = [];
    this.form.reset({
      sortDate: new Date(),
      length: '1.0',
      width: '1.0',
      height: '1.0',
    });
  }

  /**
   * get form value changes
   *
   * @memberof WorldWideExpressFreightTableComponent
   */
  formValueChanges() {
    this.form
      .get('destination')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((selectedCode) => {
        const selectedCountry = this.countryLimits().find(
          (country) => country.countryCode === selectedCode,
        );
        this.selectedCountry.set(selectedCountry);
        this.updateValidations(['weight'], [Validators.required]);
      });

    this.form.get('trackingNumbers')?.valueChanges.subscribe(() => {
      this.isFormSubmitted.set(false);
    });
    this.form.get('lb')?.valueChanges.subscribe((value) => {
      const lbValue = Number(value);
      const weight = Number(this.form.get('weight')?.value);
      if (!weight && lbValue) {
        const validations = [Validators.required];
        this.updateValidations(['weight'], validations);
        this.isFormSubmitted.set(false);
      }
    });
  }

  /**
   * get country limits
   *
   * @memberof WorldWideExpressFreightTableComponent
   */
  getCountryLimits() {
    this._revenueRecoveryService
      .getCountryLimits()
      .pipe(
        take(1),
        map((response) => {
          this.countryLimits.set(response?.response?.countryLimits || []);
          const countryList = this.countryLimits().map((country) => ({
            label: `${country.countryName}-${country.countryCode}`,
            value: country.countryCode,
          }));

          this.countryList.set(countryList);
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      )
      .subscribe();
  }

  setSortCodeOption() {
    this.sortCodeOption = [];
    this.dataSource.data.forEach((item) => {
      const isReasonTypeAdded = this.sortCodeOption.find(
        (i) => i.id === item.sortCode,
      );
      if (!isReasonTypeAdded) {
        this.sortCodeOption.push({
          id: item.sortCode,
          selected: true,
        });
      }
    });
  }

  setDateRangeValidation() {
    const dates = this.dataSource.data.map((item) =>
      new Date(item.sortDate).getTime(),
    );
    // Find min date
    this.tableMinSortDate = new Date(Math.min(...dates));

    // Find max date
    this.tableMaxSortDate = new Date(Math.max(...dates));

    // enabled dates
    this.enabledDates = dates.map((d) => new Date(d));
  }

  initForm() {
    this.form = this.fb.group({
      sortDate: [new Date(), Validators.required],
      sortCode: [null, Validators.required],
      trackingNumbers: [
        '',
        Validators.compose([
          Validators.required,
          PtnRangePackageValidator.trackingNumberValidator(),
        ]),
      ],
      destination: [null, Validators.required],
      length: ['1.0', Validators.required],
      width: ['1.0', Validators.required],
      height: ['1.0', Validators.required],
      weight: [null, Validators.required],
      lb: [null],
    });
  }

  /**
   * Filter by text column
   *
   * @param {*} filters
   * @param {*} data
   * @param {string} filterKey
   * @return {*}  {boolean}
   * @memberof WorldWideExpressFreightTableComponent
   */
  filterByTextColumn(filters: WWEF, data: WWEF, filterKey: string): boolean {
    const value = filters[filterKey as keyof WWEF];
    const rowValue = data[filterKey as keyof WWEF];
    const searchData = rowValue?.toString().toLowerCase() || '';
    const searchValue = value?.toString().toLowerCase();
    if (searchValue) {
      return searchValue.includes(searchData);
    } else {
      return searchData ? searchData.includes(searchValue) : false;
    }
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {AdminTableColumn} field
   * @memberof WorldWideExpressFreightTableComponent
   */
  onStatusChangeForDropdowns($event: string | string[], field: TableColumn) {
    field.ids[0] = $event as unknown as Items;
    if (field.itemId === ColumnIds.SORT_CODE) {
      this.sortCode.formControl.setValue($event);
    }
    this.search();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof WorldWideExpressFreightTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [this.trackingNumber, this.sortDate],
      this.dataSource,
      [this.sortCode],
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof WorldWideExpressFreightTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.tableData(),
      [this.trackingNumber.formControl, this.sortDate.formControl],
      [this.sortCode],
    );
    this.resetSelectDropdowns.next(true);
  }

  /**
   * On clicking submit button emit the button click to be handled in parent
   *
   * @memberof WorldWideExpressFreightTableComponent
   */
  submit() {
    const { length, width, height, weight, lb } = this.form.value;
    const trackingNumbers = this.form.value.trackingNumbers.split(',');
    const validations = [Validators.required, nonZeroValidator()];
    const lbValue = Number(lb);
    const weightValue = Number(weight);
    const fields = ['length', 'width', 'height', 'weight'];

    // Remove 'weight' if the condition is met
    const updatedFields =
      !weightValue && lbValue ? fields.filter((f) => f !== 'weight') : fields;

    this.updateValidations(updatedFields, validations);

    if (
      this.currentLocation()?.location?.configurations?.gslConfigurations
        .revenueRecoveryDetails
    ) {
      const unitType = this._unitConfigurationService.unitOfMeasureSystem();
      const maxWeight =
        unitType == 'I'
          ? this.selectedCountry()?.maxWeight_I
          : this.selectedCountry()?.maxWeight_M;
      const defaultValidation =
        !weightValue && lbValue ? [Validators.required] : validations;
      const updatedValidation = [
        ...defaultValidation,
        maxValidator(maxWeight as number),
      ];
      this.updateValidations(['weight'], updatedValidation);
    }
    this.isFormSubmitted.set(true);

    if (this.form.invalid) {
      return;
    }

    const newRecords = trackingNumbers.map((trackingNumber: string) => {
      const item: WWEF = {
        sortDate: formatDate(
          this.form.value.sortDate,
          'MMMM dd, yyyy',
          'en-US',
        ),
        sortCode: this.form.value.sortCode,
        trackingNumber: trackingNumber.toUpperCase(),
        destination: this.form.value.destination,
        length,
        width,
        height,
        weight,
        uowType: 'Pallet',
      };
      return item;
    });

    this.submitRequest(newRecords);
  }

  updateValidations(fields: string[], validations: any[]) {
    fields.forEach((field) => this.setCustomValidation(field, validations));
  }

  /**
   * open dimension Exceed modal
   *
   * @memberof WorldWideExpressFreightTableComponent
   */
  dimensionExceed(wwef: WWEF[], message: string) {
    const body = `${message.replaceAll('.0', '')}<br/><br/>${
      CONST.DIMENSIONS_EXCEEDED.PROCEED
    }`;
    this.commonUtils
      .openWarningModal({
        header: CONST.DIMENSIONS_EXCEEDED.HEADER,
        body: body,
        confirmButtonText: CONST.DIMENSIONS_EXCEEDED.CONFIRM_BUTTON,
        rejectButtonText: CONST.DIMENSIONS_EXCEEDED.REJECT_BUTTON,
        extraClass: 'large-confirm-btn',
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.button === BUTTON_CLICK.ACCEPT) {
          this.submitRequest(wwef, true);
        }
      });
  }

  formatValue(value: string): string {
    const numValue = Number(value);
    return value.includes('.') ? value : numValue.toFixed(1);
  }

  submitRequest(wwef: WWEF[], dimensionExceed = false) {
    const trackingNumber = wwef[0].trackingNumber;
    const intercept = this.intercepts().find(
      (item) => item.trackingNumber === trackingNumber,
    );
    const isLocalIntercept = intercept?.interceptType === 'LOC';

    const weight = this.form.value.weight;
    const lb = this.form.value.lb;
    const revenueRecoveryRequest = {
      sortDate: formatDate(wwef[0].sortDate, 'yyyy-MM-dd', 'en-US'),
      sortCode: wwef[0].sortCode,
      trackingNumber,
      intercept: !!(intercept && !isLocalIntercept),
      localIntercept: isLocalIntercept,
      sizeAudit: null,
      weightAudit: null,
      wWEF: {
        iweight: Number(weight),
        fweight: Number(lb),
        length: this.form.value.length,
        width: this.form.value.width,
        height: this.form.value.height,
        destCC: this.form.value.destination,
        pallet: !!this.form.value.pallet,
        wwef: dimensionExceed,
      },
    } as RevenueRecoveryRequest;
    const { length, width, height } = this.form.value;

    const wwefItem = revenueRecoveryRequest.wWEF as unknown as WWEFItem;
    const updateWWEF = [
      {
        ...wwef[0],
        length: this.formatValue(length),
        width: this.formatValue(width),
        height: this.formatValue(height),
        weight: `${wwefItem?.iweight}.${wwefItem?.fweight}`,
      } as unknown as WWEF,
    ];
    if (intercept && !dimensionExceed) {
      return this.openPackageInterceptPopup(
        revenueRecoveryRequest,
        updateWWEF,
        isLocalIntercept,
      );
    }
    this.getWWEF(revenueRecoveryRequest, updateWWEF);
  }

  getWWEF(revenueRecoveryRequest: RevenueRecoveryRequest, wwef: WWEF[]) {
    this._revenueRecoveryService
      .getRevenueRecovery(revenueRecoveryRequest, 3)
      .pipe(
        take(1),
        map((response) => {
          const validations = [Validators.required];
          this.updateValidations(
            ['length', 'width', 'height', 'weight'],
            validations,
          );
          if (response?.status === 'WARNING') {
            this.dimensionExceed(wwef, response.message);
            return;
          }
          if (response?.code === 200) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            const index = this.tableData().findIndex(
              (i) => i.trackingNumber === wwef[0].trackingNumber,
            );

            if (index > -1) {
              this.tableData.update((current) => {
                current[index] = wwef[0];
                return [...current];
              });
            } else {
              this.tableData.update((current) => {
                return [{ ...wwef[0] }, ...current];
              });
            }
            this.dataSource.data = this.tableData();
            this.setSortCodeOption();
            this.setDateRangeValidation();
            this.form.reset({
              sortDate: this.commonUtils.getDateFromString(
                revenueRecoveryRequest.sortDate,
              ),
              length: '1.0',
              width: '1.0',
              height: '1.0',
            });
          }
        }),
      )
      .subscribe();
  }

  openPackageInterceptPopup(
    revenueRecoveryRequest: RevenueRecoveryRequest,
    formData: WWEF[],
    isLocalIntercept = false,
  ) {
    const dialogRef = this.dialog.open(
      PackageInterceptConfirmationModalComponent,
      {
        width: '24.4rem',
        disableClose: true,
        data: {
          trackingNumber: revenueRecoveryRequest.trackingNumber,
          isLocalIntercept,
        },
      },
    );
    const wwefItem = revenueRecoveryRequest.wWEF as unknown as WWEFItem;
    dialogRef?.afterClosed().subscribe((resp) => {
      if (resp === 'yes') {
        this.getWWEF(revenueRecoveryRequest, formData);
      } else {
        this.form.reset({
          sortDate: this.commonUtils.getDateFromString(
            revenueRecoveryRequest.sortDate,
          ),
          sortCode: revenueRecoveryRequest.sortCode,
          length: wwefItem.length,
          width: wwefItem.width,
          height: wwefItem.height,
          destination: wwefItem.destCC,
          weight: wwefItem.iweight,
          lb: wwefItem.fweight,
        });
      }
    });
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof WorldWideExpressFreightTableComponent
   */
  get isFilterEnabled() {
    return (
      this.trackingNumber.formControl.value !== '' ||
      this.sortDate.formControl.value !== '' ||
      this.sortCode.ids.length > 0
    );
  }

  /**
   *
   * Create filter for table
   * @private
   * @return {*}  {(data: any, filter: string) => boolean}
   * @memberof WorldWideExpressFreightTableComponent
   */
  private createFilter(): (data: WWEF, filter: string) => boolean {
    return (data: WWEF, filter: string) => {
      const filters = JSON.parse(filter);

      return Object.keys(filters).every((filterKey) => {
        //Allows opening of row if filters are applied already
        if (filters[filterKey] === '') {
          return true;
        }
        return this.filterByColumn(filterKey, data, filters);
      });
    };
  }

  /**
   * Filter column by column id
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof WorldWideExpressFreightTableComponent
   */
  private filterByColumn(filterKey: string, data: WWEF, filters: WWEF) {
    switch (filterKey) {
      case ColumnIds.SORT_DATE:
        if (filters.sortDate !== '') {
          const filteredStartDate = new Date(filters.sortDate);
          const dataDate = new Date(data.sortDate);
          if (dataDate.getTime() === filteredStartDate.getTime()) {
            return true;
          }
        }
        return false;
      case ColumnIds.SORT_CODE:
        return this.filterByTextColumn(filters, data, filterKey);

      default:
        return this.setDefaults(filters, data, filterKey);
    }
  }

  /**
   * return default values
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof WorldWideExpressFreightTableComponent
   */
  private setDefaults(filters: WWEF, data: WWEF, filterKey: string) {
    const value = filters[filterKey as keyof WWEF];
    const rowValue = data[filterKey as keyof WWEF];
    const searchData = rowValue?.toString().toLowerCase() || '';
    const searchValue = value?.toString().toLowerCase();
    return searchData.includes(searchValue);
  }

  /**
   *
   * Set custom validation
   * @private
   * @param {string} field
   * @param {ValidatorFn[]} validators
   */
  private setCustomValidation(field: string, validators: ValidatorFn[]) {
    this.form.get(field)?.setValidators(validators);
    this.form.get(field)?.updateValueAndValidity();
  }
}
